import { useEffect, useState } from 'react';
import { sendReportByEmail } from '../../api/brandingApi';

interface Props {
  scanId: string;
  domain: string;
  defaultEmail?: string;
  /** 'post-scan' (default): offered after a completed scan.
   *  'slow-path': shown while scan is still running — email used for notification. */
  variant?: 'post-scan' | 'slow-path';
}

type SendState = 'idle' | 'sending' | 'sent' | 'error';

export default function EmailReportCapture({ scanId, domain, defaultEmail = '', variant = 'post-scan' }: Props) {
  const [email, setEmail]       = useState('');
  const [state, setState]       = useState<SendState>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    setEmail(typeof defaultEmail === 'string' ? defaultEmail : '');
  }, [defaultEmail]);

  const safeEmail = typeof email === 'string' ? email : '';
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(safeEmail.trim());

  async function handleSend() {
    if (!isValidEmail) return;
    setState('sending');
    setErrorMsg('');
    try {
      await sendReportByEmail(scanId, safeEmail.trim());
      setState('sent');
    } catch {
      setState('error');
      setErrorMsg('Could not register your email. Please try again in a moment.');
    }
  }

  if (state === 'sent') {
    return (
      <div className="asneris-alert asneris-alert--success" style={{ marginTop: '24px' }}>
        <p className="asneris-alert-title">
          {variant === 'slow-path'
            ? <>Got it! We'll email <strong>{email}</strong> as soon as your {domain} report is ready.</>
            : <>Report sent — check <strong>{email}</strong> for your {domain} analysis.</>
          }
        </p>
      </div>
    );
  }

  const isSlowPath = variant === 'slow-path';

  return (
    <div
      className={`asneris-tip${isSlowPath ? ' asneris-tip-warning' : ''}`}
      style={{ marginTop: '24px', flexDirection: 'column', gap: '4px', maxWidth: 'none' }}
    >
      <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: '14px' }}>
        {isSlowPath
          ? `Your ${domain} report is being prepared`
          : 'Save your report — get it in your inbox'
        }
      </p>
      <p style={{ margin: '0 0 12px', fontSize: '13px', color: 'var(--asneris-muted)' }}>
        {isSlowPath
          ? `Enter your email and we'll send the full ${domain} analysis the moment it's ready — free, no account needed.`
          : `Your ${domain} report is ready. Enter your email to get the full breakdown — free, no account required.`
        }
      </p>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginTop: '8px' }}>
        <div className="asneris-field" style={{ flex: '1 1 0', minWidth: 0 }}>
          <input
            className="asneris-input"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setState('idle'); }}
            disabled={state === 'sending'}
            aria-label="Email address for report"
            style={{ height: '40px', fontSize: '14px' }}
          />
        </div>
        <button
          className={`asneris-btn ${isSlowPath ? '' : 'asneris-btn-primary'} asneris-btn-sm`}
          type="button"
          onClick={handleSend}
          disabled={!isValidEmail || state === 'sending'}
          style={{ flexShrink: 0, height: '40px' }}
        >
          {state === 'sending'
            ? <><span className="asneris-spinner asneris-spinner--sm" style={{ marginRight: '6px' }} />Saving…</>
            : isSlowPath ? 'Notify Me' : 'Send Report'
          }
        </button>
      </div>

      {state === 'error' && (
        <div className="asneris-alert asneris-alert--error" style={{ marginTop: '12px', padding: '10px 14px' }}>
          <p className="asneris-alert-body">{errorMsg}</p>
        </div>
      )}
    </div>
  );
}
